/**
 * V78.1: 대체휴무 표기 공용 formatter
 *
 * 표기 규칙:
 * - 1일 = 8시간 (기존 시스템 표준 상수 COMP_OFF_HOURS_PER_DAY=8과 일치)
 * - 소수 days → 총 시간으로 환산 후 정수 시간 반올림 (Math.round)
 * - 결과를 아래 형식으로 출력:
 *   - `N일 N시간` (일/시간 모두 있을 때)
 *   - `N일` (시간 없이 딱 일 단위)
 *   - `N시간` (1일 미만)
 *   - `0시간` (null/undefined/0)
 *
 * 예시 (hoursPerDay=8 기준):
 *  - 0        → "0시간"
 *  - 0.125    → "1시간"
 *  - 0.5      → "4시간"
 *  - 1.0      → "1일"
 *  - 1.25     → "1일 2시간"
 *  - 2.5      → "2일 4시간"
 *
 * 근거:
 * - 신규 지급(V78)은 시간 단위 정수로 입력되므로 환산 시 소수 드리프트 없음
 * - 레거시 0.1 단위 데이터는 0.1 × 8 = 0.8h → 반올림 1시간으로 표시 (소폭 드리프트 허용)
 * - 반올림 기준을 "시간"으로 고정하여 표기 일관성 확보
 */

const DEFAULT_HOURS_PER_DAY = 8;

/**
 * 대체휴무 days → "N일 N시간" 포맷
 * @param days 대체휴무 일수 (소수 허용)
 * @param hoursPerDay 1일 기준 시간 (기본 8). 직원별 근로시간 전달 가능.
 */
export function formatCompOffDaysHours(
  days: number | null | undefined,
  hoursPerDay: number = DEFAULT_HOURS_PER_DAY
): string {
  if (days == null || isNaN(days)) return '0시간';
  if (hoursPerDay <= 0) hoursPerDay = DEFAULT_HOURS_PER_DAY;

  // V78.4: 음수(사용) 처리 — 절대값으로 계산 후 부호 복원
  const sign = days < 0 ? '-' : '';
  const absDays = Math.abs(days);

  // V69 (2026-06-10): Math.round → Math.floor (8h=1d 일관 정책 — 1시간 미만 round-off 잔재 흡수 차단)
  const totalHours = Math.floor(absDays * hoursPerDay);
  if (totalHours <= 0) return '0시간';

  const wholeDays = Math.floor(totalHours / hoursPerDay);
  const remainingHours = totalHours % hoursPerDay;

  if (wholeDays > 0 && remainingHours > 0) return `${sign}${wholeDays}일 ${remainingHours}시간`;
  if (wholeDays > 0) return `${sign}${wholeDays}일`;
  return `${sign}${remainingHours}시간`;
}

/**
 * 증분/차감 delta 표기 (부호 포함)
 * @example formatCompOffDelta(-0.5) → "-4시간"
 */
export function formatCompOffDelta(
  days: number | null | undefined,
  hoursPerDay: number = DEFAULT_HOURS_PER_DAY
): string {
  if (days == null || isNaN(days) || days === 0) return '0시간';
  const sign = days < 0 ? '-' : '+';
  return `${sign}${formatCompOffDaysHours(Math.abs(days), hoursPerDay)}`;
}
